'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

export const PageTransitionStatusContext = createContext<
  'load' | 'ready' | 'auto'
>('auto');

/**
 * Safety net for `onLoad` events that never get a matching `onReady`.
 *
 * The document click listener fires `onLoad` for every internal link click. The
 * matching `onReady` only fires when `pathname` / `searchParams` actually change.
 * If a click doesn't translate into a real route change — for example, when the
 * target route fails to mount, or when in-page logic intercepts the click and
 * opens a modal instead of navigating (see Mein Schiff ship-manifest PIN flow
 * inside the iOS in-app WebView) — the indicator would otherwise stay "load"
 * forever, hiding the page under a full-screen overlay. We give the navigation
 * this many milliseconds to materialise, then force `ready` if nothing happened.
 */
const LOAD_SAFETY_TIMEOUT_MS = 5000;

export const usePageTransition = (
  onTransitionStatus: (status: 'load' | 'ready') => void,
) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const contextStatus = useContext(PageTransitionStatusContext);

  // useRef instead of useState to avoid unnecessary re-renders
  const lastStatus = useRef<'load' | 'ready' | undefined>(undefined);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const onLoad = useCallback(() => {
    if (lastStatus.current === 'load') {
      return;
    }

    lastStatus.current = 'load';
    onTransitionStatus('load');

    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    safetyTimeoutRef.current = setTimeout(() => {
      if (lastStatus.current === 'load') {
        lastStatus.current = 'ready';
        onTransitionStatus('ready');
      }
    }, LOAD_SAFETY_TIMEOUT_MS);
  }, [onTransitionStatus]);

  const onReady = useCallback(() => {
    if (lastStatus.current === 'ready') {
      return;
    }

    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = undefined;
    }

    lastStatus.current = 'ready';
    onTransitionStatus('ready');
  }, [onTransitionStatus]);

  useEffect(() => {
    if (contextStatus === 'auto') {
      return;
    }

    onTransitionStatus(contextStatus);
  }, [contextStatus, onTransitionStatus]);

  /**
   * Detect navigation events by listening to all link clicks on the document.
   * This is a workaround because Next.js does not provide any way to hook into the
   * router events. With Next.js 15, the options are even more limited than before.
   */
  useEffect(() => {
    if (contextStatus !== 'auto') {
      return;
    }

    const handler = (event: Event) => {
      if (event.target instanceof HTMLElement) {
        const anchor = event.target.closest<HTMLAnchorElement>('a[href]');

        if (!anchor || (anchor.target !== '_self' && anchor.target !== ''))
          return;

        // Ignore links with data-no-page-transition attribute
        if (anchor.hasAttribute('data-no-page-transition')) return;

        // Ignore hash-only links (e.g., href="#" or href="#something")
        const href = anchor.getAttribute('href');
        if (href?.startsWith('#')) return;

        const path = extractSameOriginPath(anchor.href);

        // Ignore external links and links with mxp parameter
        if (!path || !path.startsWith('/') || path.includes('mxp=')) return;

        onLoad();
      }
    };

    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextStatus, onLoad]);

  /**
   * Listen for route ready.
   * This is the recommended way for Next.js (see doc link above).
   */
  useEffect(() => {
    if (contextStatus !== 'auto') {
      return;
    }

    setTimeout(() => onReady(), 100);
  }, [pathname, searchParams, onReady, contextStatus]);

  // Clear the safety timeout on unmount so we don't leak a setTimeout.
  useEffect(() => {
    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

const extractSameOriginPath = (href: string): string | undefined => {
  try {
    const url = new URL(href);
    if (url.origin !== window.location.origin) return undefined;

    return url.pathname + url.search;
  } catch {
    return href;
  }
};
